import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from './Navbar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import EmployeeFormModal from './EmployeeFormModal';
import EmployeeBiodataModal from './EmployeeBiodataModal';
import { Employee } from '../types/employee';
import { exportEmployeeToExcel } from '../utils/excelExport';
import ImagePreview from './ImagePreview';

const pageSizeDefault = 10;

const EmployeeDetailsPage: React.FC = () => {
  const [items, setItems] = useState<Employee[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [bioEmployeeId, setBioEmployeeId] = useState<number | null>(null);
  const [showBioModal, setShowBioModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/employees', { 
        params: { page, pageSize: pageSizeDefault, search: search || undefined } 
      });
      const data = res.data?.data ?? res.data;
      setItems(data.items || []);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      console.error('Failed to load employees', e);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { 
    fetchData();
  }, [fetchData]);

  // Only update search when form is submitted
  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
    // fetchData(); // fetchData is already called by useEffect when search changes
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const res = await api.get('/employees', { 
        params: { 
          page: 1,
          pageSize: 100000, // Large number to get all records
          search: search || undefined 
        } 
      });
      
      const data = res.data?.data ?? res.data;
      if (data?.items?.length) {
        exportEmployeeToExcel(data.items, search);
      } else {
        alert('No data available to export');
      }
    } catch (error) {
      console.error('Failed to download data:', error);
      alert('Failed to download data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const { user } = useAuth();

  const handleAdd = () => {
    setSelectedEmployee(null);
    setShowModal(true);
  };

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await api.delete(`/employees/${id}`);
      fetchData();
    } catch (err) {
      console.error('Failed to delete employee:', err);
      alert('Failed to delete employee');
    }
  };

  const handleSave = async (formData: FormData) => {
    try {
      if (selectedEmployee) {
        await api.put(`/employees/${selectedEmployee.id}`, formData);
      } else {
        await api.post('/employees', formData);
      }
      
      setShowModal(false);
      fetchData(); // Refresh list after save
    } catch (err: any) {
      console.error('Failed to save employee:', err);
      alert(err.response?.data?.message || 'Failed to save employee. Please try again.');
      throw err;
    }
  };

  const handleShow = (employeeId: number) => {
    setBioEmployeeId(employeeId);
    setShowBioModal(true);
  };

  const canPrev = useMemo(() => page > 1, [page]);
  const canNext = useMemo(() => page < totalPages, [page, totalPages]);

  const calculateAge = (dob?: string | null) => {
    if (!dob) return null;
    const b = new Date(dob);
    if (isNaN(b.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - b.getFullYear();
    const m = today.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < b.getDate())) {
      age--;
    }
    return age >= 0 ? age : 0;
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      <Navbar />
      <div className="w-full mx-auto px-0 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4">
          <h1 className="text-3xl font-serif text-gray-900">Employee Details</h1>
        </div>

        {/* Search and Actions */}
        <div className="mb-6 px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <form onSubmit={onSearch} className="relative">
                <input
                  className="w-[240px] md:w-[340px] lg:w-[420px] rounded-full border-2 border-[#2f6b54] px-4 py-2 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#2f6b54]"
                  placeholder="Search Employee"
                  defaultValue={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button
                  type="submit"
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-[#2f6b54] text-white rounded-full w-10 h-10 flex items-center justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </button>
              </form>
            </div>

            <div className="flex items-center gap-3">
              <button 
                className="btn-primary"
                onClick={handleDownload}
              >
                Download List
              </button>
              <button 
                className="btn-primary"
                onClick={handleAdd}
              >
                Add Employee
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-x-auto border border-[#2f6b54] mx-4">
          <table className=" table-auto border-collapse min-w-full minWidthtable">
            <thead>
              <tr className="bg-[#f1f6f1]">
                <th className="px-2 py-1 border border-[#2f6b54] text-center action-col">Actions</th>
                <th className="px-2 py-1 border border-[#2f6b54] text-center Srn">Sr.No.</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Name</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Mobile No.</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Current Address</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Residential Address</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Caste</th>
                <th className="px-4 py-1 border border-[#2f6b54]">User Photo</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Date of Birth</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Age</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Education</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Identification Mark</th>
                <th className="px-4 py-1 border border-[#185a41]">Work Place</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Joining Date</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Exit Date</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Father's Name</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Father's Mobile</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Mother's Name</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Mother's Mobile</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Pradhan Name</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Pradhan Mobile No.</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Police Station</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Police Station Mobile No.</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Aadhar Card Number</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Election Card Number</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Pan Card Number</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Aadhar Card Photo</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Election Card Photo</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Pan Card Photo</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Pan Card Returned</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Account No.</th>
                <th className="px-4 py-1 border border-[#2f6b54]">IFSC Code</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Bank Name</th>
                <th className="px-4 py-1 border border-[#2f6b54]">Branch Name</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={34} className="text-center py-4">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={34}>
                    <div className="mx-4 my-2 px-4 py-3 bg-[#ecf8f1] border border-[#14532d] rounded text-center text-[#14532d]">
                      <div className="text-base font-medium">No employees found</div>
                      <div className="text-sm mt-1 text-green-800/80">Try adjusting your search or add a new employee.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((employee, idx) => (
                  <tr key={employee.id} className="hover:bg-gray-50 h-20 align-middle">
                    <td className="px-2 py-2 border border-[#e6efe6] text-center action-col">
                      <div className="inline-flex items-center gap-2 justify-center">
                        {user?.isAdmin ? (
                          <>
                            <button 
                              onClick={() => handleEdit(employee)}
                              className="p-2 bg-[#0b5b3b] text-white rounded-full"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M16.5 3.964a2.121 2.121 0 013 3L7 19.5H4v-3L16.5 3.964z" />
                              </svg>
                            </button>
                            <button 
                              onClick={() => handleDelete(employee.id)}
                              className="p-2 bg-red-600 text-white rounded-full"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <div></div>
                          // <div className="text-sm text-gray-500">No edit rights</div>
                        )}
                        <button
                          onClick={() => handleShow(employee.id)}
                          className="p-2 bg-blue-600 text-white rounded-full"
                          title="Show"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-2 border border-[#e6efe6] text-center w-[6px] Srn">{(page - 1) * pageSizeDefault + idx + 1}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[180px] truncate" title={`${employee.firstName} ${employee.surname}`}>{`${employee.firstName} ${employee.surname}`}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[120px] truncate" title={employee.mobileNumber}>{employee.mobileNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[220px] truncate" title={employee.currentAddress}>{employee.currentAddress || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[220px] truncate" title={employee.residentialAddress}>{employee.residentialAddress || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.caste || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">
                      {employee.userPhoto &&
                        <ImagePreview
                          src={employee.userPhoto}
                          alt={`user-${employee.id}`}
                          className="w-20 h-20 object-cover rounded"
                        />
                      }
                    </td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.dateOfBirth ? new Date(employee.dateOfBirth).toLocaleDateString() : 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.dateOfBirth ? calculateAge(employee.dateOfBirth) : '0'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[140px] truncate" title={employee.education}>{employee.education || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[200px] truncate" title={employee.identificationMark}>{employee.identificationMark || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[160px] truncate" title={employee.workPlace}>{employee.workPlace || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString() : 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.exitDate ? new Date(employee.exitDate).toLocaleDateString() : 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[160px] truncate" title={employee.fatherName}>{employee.fatherName || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[120px] truncate" title={employee.fatherMobileNumber}>{employee.fatherMobileNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[160px] truncate" title={employee.motherName}>{employee.motherName || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[120px] truncate" title={employee.motherMobileNumber}>{employee.motherMobileNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[160px] truncate" title={employee.pradhanName}>{employee.pradhanName || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[120px] truncate" title={employee.prdhanMobile}>{employee.prdhanMobile || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[160px] truncate" title={employee.policeStation}>{employee.policeStation || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6] max-w-[120px] truncate" title={employee.policeMobile}>{employee.policeMobile || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.aadharNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.electionNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.panNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">
                      {employee.aadharCardPhoto &&
                        <ImagePreview
                          src={employee.aadharCardPhoto}
                          alt={`aadhar-${employee.firstName}`}
                          className="w-20 h-14 object-cover rounded"
                        />
                      }
                    </td>
                    <td className="px-4 py-2 border border-[#e6efe6]">
                      {employee.electionCard &&
                        <ImagePreview
                          src={employee.electionCard}
                          alt={`election-${employee.firstName}`}
                          className="w-20 h-14 object-cover rounded"
                        />
                      }
                    </td>
                    <td className="px-4 py-2 border border-[#e6efe6]">
                      {employee.panCardPhoto &&
                        <ImagePreview
                          src={employee.panCardPhoto}
                          alt={`pan-${employee.firstName}`}
                          className="w-20 h-14 object-cover rounded"
                        />
                      }
                    </td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.panCardReturned ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.accountNumber || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.ifscCode || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.bankName || 'NA'}</td>
                    <td className="px-4 py-2 border border-[#e6efe6]">{employee.bankBranch || 'NA'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-end gap-3 mt-4 px-4">
          <button className="btn-secondary" disabled={!canPrev} onClick={() => setPage(p => Math.max(1, p-1))}>Previous</button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button className="btn-primary" disabled={!canNext} onClick={() => setPage(p => p+1)}>Next</button>
        </div>

        {/* Modal */}
        {showModal && (
          <EmployeeFormModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
            editData={selectedEmployee}
          />
        )}

        {showBioModal && (
          <EmployeeBiodataModal
            isOpen={showBioModal}
            onClose={() => { setShowBioModal(false); setBioEmployeeId(null); }}
            employeeId={bioEmployeeId}
          />
        )}
      </div>
    </div>
  );
};

export default EmployeeDetailsPage;
